import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { GetChartOfAccountById, ChartOfAccountArchive } from "../../../../services/AccountsApi";
import ArchivePopup from "../../../../components/popup/ArchivePopup/ArchivePopup";
import ArchiveBanner from "../../../../components/common/ArchiveBanner/ArchiveBanner";
import { ArchiveAlert } from "../../../../components/common/ErrorAlert/ArchiveAlert";
import { showAlert } from "../../../../components/common/ErrorAlert/ErrorAlert";
import PageHeader from "../../../../components/common/PageHeader/PageHeader";
import CloseButton2 from "../../../../components/common/CloseButton/CloseButton2";
import InputBoxLarge from "../../../../components/common/InputBox/InputBoxLarge";
import TextBox from "../../../../components/common/TextBox/TextBox";
import ActivityLogIcon from "../../../../components/common/Action-icons/ActivityLogIcon";
import "./ChartOfaccounts.css";
import DocumentActions from "../../../../components/common/Action-icons/DocumentActions";
import EditButton from "../../../../components/common/EditButton/EditButton";
import WithLoading from "../../../../components/common/WithLoading/WithLoading";
import { usePageLoading } from "../../../../store/useLoadingStore";

const DetailChartOfAccounts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [animateEdit, setAnimateEdit] = useState(false);
  const [isLoading, setIsLoading] = usePageLoading();
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [accountData, setAccountData] = useState(null);

  const [formData, setFormData] = useState({
    code: "",
    account_name: "",
    account: "",
    income_expense_type: "",
    description: "",
    is_sub_account: false,
    parent_account: "",
  });

  const fetchChartAccountDetails = useCallback(async (accountId) => {
    setIsLoading(true);
    try {
      const response = await GetChartOfAccountById(accountId);
      if (response?.data) {
        const data = response.data;
        setFormData({
          code: data.code || "",
          account_name: data.account_name || "",
          account: data.account?.name || "",
          income_expense_type: data.income_expense_type || "",
          description: data.description || "",
          is_sub_account: data.is_sub_account || false,
          parent_account: data.parent_account
            ? `${data.parent_account.code} - ${data.parent_account.account_name}`
            : "",
          id: data.id || "",
        });
        setAccountData(data);
      }
    } catch {
      // Ignored
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    if (id) {
      fetchChartAccountDetails(id);
    }
  }, [id, fetchChartAccountDetails]);

  const handleClose = useCallback(() => {
    navigate("/accounts/configuration/chart-of-account");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    setAnimateEdit(true);
    setTimeout(() => setAnimateEdit(false), 800);
    navigate(`/accounts/configuration/chart-of-account/edit/${id}`);
  }, [navigate, id]);

  const handleArchiveClick = () => {
    setIsArchivePopupOpen(true);
  };

  const closePopups = () => {
    setIsArchivePopupOpen(false);
  };

  const HandleArchive = async () => {
    if (!formData?.id) return;
    setIsLoading(true);
    try {
      const response = await ChartOfAccountArchive(formData.id, null);
      if (response?.data) {
        closePopups();
        await fetchChartAccountDetails(id);
        ArchiveAlert(
          accountData?.is_archived
            ? t("Chart Of Account unarchived successfully!")
            : t("Chart Of Account archived successfully!"),
          "success",
        );
      }
    } catch (error) {
      showAlert(error.response?.data?.message || t("Something went wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="whole-container">
      {/* Header */}
      <div className="path-header">
        <PageHeader
          breadcrumbs={[
            { label: "Accounts" },
            { label: "Configurations" },
            {
              label: "Chart of Accounts",
              path: "/accounts/configuration/chart-of-account",
            },
            { label: "Details" },
          ]}
        />
      </div>
      {/* Title & Close Button */}
      <div className="page-actions-bar">
        <div className="name-utility">
          <div className="title-heading">{t("Chart Of Account Details")}</div>
          <WithLoading>
            <DocumentActions
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t("Actions")}
              onArchive={handleArchiveClick}
              isArchived={accountData?.is_archived || false}
            />
          </WithLoading>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="2"
            height="17"
            viewBox="0 0 2 17"
            fill="none"
          >
            <path d="M1 0V17" stroke="#CDCDCD" />
          </svg>
          <ActivityLogIcon data-tooltip-id="global-tooltip" data-tooltip-content={t("Activity Log")}
            objectId={formData.id}
            modelName="OrganizationChartOfAccounts"
            name="Chart Of Account"
          />
        </div>
        <div className="page-name-rightside-icons">
          <EditButton data-tooltip-id="global-tooltip" data-tooltip-content={t("Edit")} onClick={handleEdit} animate={animateEdit} />
          <CloseButton2 data-tooltip-id="global-tooltip" data-tooltip-content={t("Close")} onClick={handleClose} />
        </div>
      </div>

      <ArchiveBanner
        isArchived={accountData?.is_archived || false}
        documentName={t("chart of account")}
        onRestore={handleArchiveClick}
      />

      {/* Form Section */}
      <div className="penalty-section">
        <div className="penalty-fields-card chart-fields-card box-pad">
          <InputBoxLarge label="Code" defaultValue={formData.code} readOnly />
          <InputBoxLarge
            label="Account Name"
            defaultValue={formData.account_name}
            readOnly
          />
          <InputBoxLarge
            label="Account Type"
            defaultValue={formData.account}
            readOnly
          />
          {/* <InputBoxLarge label="incomexpensetype" defaultValue={formData.income_expense_type} readOnly /> */}
          {formData.income_expense_type && (
            <InputBoxLarge
              label="Income / Expense Type"
              defaultValue={formData.income_expense_type}
              readOnly
            />
          )}

          <InputBoxLarge
            label="Is Sub Account"
            defaultValue={formData.is_sub_account ? "Yes" : "No"}
            readOnly
          />
          {formData.is_sub_account && (
            <InputBoxLarge
              label="Parent Account"
              defaultValue={formData.parent_account}
              readOnly
            />
          )}
          <TextBox
            label="Description"
            defaultValue={formData.description}
            readOnly
          />
        </div>
      </div>
      <WithLoading>
        <ArchivePopup
          isOpen={isArchivePopupOpen}
          archivename={formData?.account_name || ""}
          onSave={HandleArchive}
          onClose={closePopups}
          isArchived={accountData?.is_archived || false}
        />
      </WithLoading>
    </div>
  );
};

export default DetailChartOfAccounts;
